import { UploadFile } from '@mui/icons-material'
import { Box, Button, Card, CardContent, CardHeader, LinearProgress, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import LocalCache from '../../backend/LocalCache'
import { ISignedURL, IUploadedResult } from '../../definitions/Upload'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import UploadManager from '../../managers/UploadManager'
import FileUpload from '../controls/FileUpload'
import useCommand from '../hooks/useCommand'
import { snackbar } from '../services/SnackbarService'

interface P {
    contractId?: number
    marginTop?: number
    onFinish: (res: IUploadedResult) => void
    /**
     * Disable upload for Editor view mode
     */
    disabled?: boolean
    contractUUID?: string
}

export default function UploadContractButton({ contractId, marginTop, onFinish, disabled, contractUUID }: P) {
    const [selectedFiles, setSelectedFiles] = useState<File[]>()
    const [modalStatus, setModalStatus] = useState<'insert' | 'loading' | 'success' | 'failed'>('insert')

    useEffect(() => {
        setModalStatus('insert')
    }, [])

    const { execute } = useCommand({
        commandFn: (numberItemsToUpload: number) => ApiClient.getSignedURLs(numberItemsToUpload),
        onFinish: result => {
            if (result.status === 'success') {
                upload(result.response, selectedFiles)
            }
        }
    })

    const upload = async (signs: ISignedURL[], files: File[]) => {
        const res = await UploadManager.uploadContractFiles(signs, files, contractId, contractUUID)
        if (Helper.hasVal(res)) {
            setModalStatus('success')
            LocalCache.setStale('contracts')
            LocalCache.setStale('files')
            snackbar.show({ severity: 'success', text: 'File uploaded successfully' })
            onFinish(res)
        } else {
            setModalStatus('failed')
        }
    }

    const handleFileSelection = (files: File[]) => {
        if (disabled) {
            snackbar.show({ severity: 'warning', text: 'You cannot upload files in a contract that is live, in review, or archived' })
            return
        }
        setSelectedFiles(files)
    }

    return (
        <Box margin={(marginTop ?? 80) + 'px auto auto'} maxWidth={640}>
            <Card>
                <CardHeader title='Upload Contract' />
                <CardContent>
                    <Box>
                        <FileUpload disable={modalStatus !== 'insert'} setSelectedFiles={handleFileSelection} />
                        {(selectedFiles && selectedFiles.length > 0) && (
                            <Box mt={3} flex={1} flexDirection='row' display='flex'>
                                <Box flex={1}>
                                    {selectedFiles?.map((file, i) => {
                                        return (
                                            <Box key={i} display='flex' flexDirection='row' alignItems='center' gap={2} mb={1}>
                                                <UploadFile color='primary' />
                                                <Box>
                                                    <Typography fontWeight={500}>{file.name}</Typography>
                                                    <Typography variant='caption'>
                                                        {Formatter.bytes(file.size, 1)}
                                                        {' • '}
                                                        {
                                                            modalStatus === 'insert'
                                                                ? 'Ready to Upload'
                                                                : (modalStatus === 'loading'
                                                                        ? 'Loading'
                                                                        : (modalStatus === 'success' ? 'Success' : 'Failed'))
                                                        }
                                                    </Typography>
                                                    {modalStatus === 'loading' && <LinearProgress sx={{ marginTop: 1 }} />}
                                                </Box>
                                            </Box>
                                        )
                                    })}
                                </Box>
                                {modalStatus === 'insert' && (
                                    <Box flex={0} justifyContent='flex-end' display='flex'>
                                        <Button
                                            variant='contained'
                                            style={{ height: 35, width: 100 }}
                                            onClick={() => {
                                                execute(selectedFiles.length)
                                                setModalStatus('loading')
                                            }}>
                                            Upload
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}
